import { FeatureCollection } from "geojson";

export namespace AgrianTypes {
	export interface Lab {
		id: string;
		name: string;
		short_name: string;
		uploadable: boolean;
		links: Record<string, any>;
	}

	export interface PlantPart {
		id: string;
		name: string;
		value: string;
	}

	export interface ColumnMap {
		name: string;
		depth: string;
		extraction_method: string;
		unit?: string;
		value_type?: string;
	}

	export interface Template {
		id: string;
		name: string;
		column_map: Record<string, ColumnMap>;
		organization_id: string;
		sample_event_type: string;
	}

	export interface Commodity {
		name_translations: Record<string, string>;
		creator_id?: number;
		updater_id?: number;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: number;
		name: string;
		icon: string;
		density?: number;
		removal_coefficients: Record<string, any>;
		color: string;
		base_commodity_id: number;
		legacy_id?: number;
		crop: boolean;
		links: Record<string, any>;
	}

	export interface User {
		creator_id: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		activation_state: string;
		company_name?: string;
		created_from: string;
		email: string;
		enforce_gdpr: boolean;
		first_name: string;
		identity_provider_id?: string;
		identity_provider_legacy_admin_rec_writer: boolean;
		inactivated_at?: string;
		job_title?: string;
		last_name: string;
		links: Record<string, any>;
		profile_ancestors_lookup: Record<string, Record<string, Record<string, string[]>>>;
		onboarding_complete: boolean;
		personal_organization_id?: string;
		primary_organizations_user_id?: string;
		resend_activation_email_url: string;
		resend_reset_password_email_url: string;
		roles: Record<string, Record<string, string[]>>[];
		suppress_emails: boolean;
		terms_of_use_accepted: boolean;
		test_account: boolean;
		working_city?: string;
		working_commodity_ids?: string[];
		working_state_id?: number;
		working_country_id?: number;
		legacy_account_type: string;
		legacy_only: boolean;
		ui_preferences: Record<string, any>[];
		has_password: boolean;
		address: Record<string, string | number | null>;
		phones: Record<string, string | number | null>[];
	}

	export interface Preference {
		id: string;
		name: string;
		value: string[] | Record<string, string | number>[];
		preferable_type: string;
		preferable_id: string;
	}

	export interface Address {
		creator_id: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		line1: string;
		line2?: string;
		city: string;
		region?: string;
		postal_code?: string;
		geo_location?: string;
		addressable: string;
		state_id: number;
		country_id: number;
	}

	export interface BoundaryMap {
		id: string;
		area?: number;
		area_common?: number;
		area_unit?: string;
		area_unit_ucum?: string;
		boundary: string;
		boundary_mappable: Record<string, any>;
		centroid?: string;
		links: Record<string, any>;
		rtk: boolean;
	}

	export interface Phone {
		id: string;
		phone_number: string;
		extension?: string;
		phone_type_id: number;
		country_id: number;
		phoneable_type: string;
		phoneable_id: string;
		created_at: string;
		updated_at: string;
		mobile_carrier_id?: string;
	}

	export interface DefaultContact {
		creator_id: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		first_name: string;
		last_name: string;
		contactable: Record<string, any>;
		email?: string;
		phones: Phone[];
	}

	export interface AlternateID {
		id: string;
		alternate_id: string;
		id_type: string;
	}

	export interface Grower {
		creator_id: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		account_number?: string;
		arbitrary_attributes: Record<string, any>;
		breadcrumbs: string[][];
		code: string;
		custom_location?: string;
		field_owner_ids: string[];
		inactivated_at?: string;
		linked_ids: string[];
		links: Record<string, any>;
		name: string;
		organization_id: string;
		active: boolean;
		simple: boolean;
		address: Address;
		boundary_map?: BoundaryMap;
		default_contact?: DefaultContact;
		alternate_ids: AlternateID[];
	}

	export interface BoundaryMappable {
		id: string;
		type: string;
	}

	export interface Farm {
		creator_id: string;
		updater_id: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		active: boolean;
		alternate_ids: string[];
		arbitrary_attributes: Record<string, string | number | boolean>;
		breadcrumbs: (string | string[])[];
		code: string;
		custom_location?: string;
		farm_water_source_ids: string[];
		grower_id: string;
		inactivated_at?: string;
		linked_ids: string[];
		links: Record<string, any>;
		name: string;
		simple: boolean;
		boundary_map: BoundaryMap;
	}

	export interface SectionLine {
		id: number;
		state_abbreviation: string;
		township: string;
		township_unit: string;
		range: string;
		range_unit: string;
		section: string;
		baseline_id: number;
		boundary: string;
	}

	export interface Field {
		creator_id: string;
		updater_id: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		alternate_ids: string[];
		arbitrary_attributes: Record<string, string | number | null>;
		area: number;
		area_common: number;
		area_unit: string;
		area_unit_ucum: string;
		breadcrumbs: (string | string[])[];
		description?: string;
		code: string;
		county_id: number;
		custom_location?: string;
		distance_location?: string;
		farm_id: string;
		field_owner_id?: string;
		growing_method_id: number;
		inactivated_at?: string;
		irrigation_source_ids: string[];
		irrigation_type_ids: string[];
		linked_ids: string[];
		links: Record<string, any>;
		mass?: number;
		mass_unit?: string;
		name: string;
		prominent_commodity_id?: number;
		rented: boolean;
		soil_capacity?: number;
		soil_capacity_unit?: string;
		soil_capacity_unit_ucum?: string;
		soil_order_id?: number;
		soil_texture_id?: number;
		state_id: number;
		unquantifiable_dimension?: number;
		unquantifiable_dimension_unit?: string;
		volume?: number;
		volume_unit?: string;
		active: boolean;
		commodity_ids: number[];
		planting_ids: string[];
		simple: boolean;
		boundary_map: BoundaryMap;
		quarter_line?: string;
		section_line: SectionLine;
	}

	export interface Organization {
		creator_id?: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		deleted_at?: string;
		id: string;
		alternate_ids: AlternateID[];
		arbitrary_attributes: Record<string, string | number | boolean | null>;
		breadcrumbs: string[];
		email?: string;
		created_as_leaf: boolean;
		logo_url: string;
		skin_logo_url: string;
		links: Record<string, any>;
		name: string;
		organization_level_id?: string;
		organization_scheme_id?: string;
		parent_id?: string;
		user_limit?: number;
		is_leaf: boolean;
		boundary_map?: string;
		address: Address;
		phones: Phone[];
	}

	export interface SoilDepth {
		column_depth: number;
		start_depth: number;
		end_depth: number;
		depth_unit: string;
		name: string;
	}

	export enum SAMPLE_METHOD {
		POINT = "Point",
		COMPOSITE = "Composite",
		ZONE = "Zone",
	}

	export interface LabPackage {
		id: string;
		name: string;
		lab_id: string;
		sample_event_type: string;
		links: Record<string, any>;
	}

	// region ZONE

	export interface Associations {
		[key: string]: any;
	}

	export interface Meta {
		[key: string]: any;
	}

	export interface ZoneMap {
		creator_id?: string;
		updater_id?: string;
		created_at: string;
		updated_at: string;
		id?: string;
		field_id?: string;
		links?: Record<string, string>;
		map_url?: string;
		min_zone_area?: number;
		name?: string;
		rate_column?: string;
		source_type?: string;
		area_units?: string;
		zone_count?: number;
		associations?: Associations;
	}

	export interface ZoneMapsResponse {
		zone_maps?: ZoneMap[];
		meta?: Meta;
	}

	export interface ZoneColumn {
		name?: string;
	}

	export interface ZoneColumnEntry {
		value?: string;
		zone_column?: ZoneColumn;
	}

	export interface Geometry {
		type: "MultiPolygon";
		coordinates: number[][][][]; // [ [ [ [lng, lat], ... ], ... ], ... ]
	}

	interface Zone {
		id?: string;
		max_value?: number;
		min_value?: number;
		zone_column_entries?: ZoneColumnEntry[];
		zone_map_id?: string;
		area?: number;
		area_units?: string;
		geometry: Geometry;
	}

	export interface ZonesResponse {
		zones?: Zone[];
	}

	/** A custom version of the ZoneMap, adding the associated zones, which contain the actual geodata */
	export interface HydratedZone extends ZoneMap {
		zones: Zone[];
	}

	// endregion

	// region SAMPLE EVENT

	export interface SubSample {
		id?: string;
		sub_sample_number?: string;
		geometry: string;
		collected_on?: any;
		marker_location?: string;
	}

	export interface ExtractionMethod {
		id?: string;
		name?: string;
		method_name?: any;
		common_name?: string;
		units?: string[];
	}

	export interface NutrientElement {
		id?: string;
		name?: string;
		short_name?: string;
		sort_order?: number;
		event_type?: string;
		extraction_methods?: string[];
		links?: Record<string, string>;
	}

	export interface SampleBase {
		id?: string;
		sample_number?: string;
		geometry: FeatureCollection;
		comments?: any;
		marker_location?: string;
		report_id?: any;
		has_lab_data?: boolean;
		links?: Record<string, string>;
		lab_packages_samples?: any[];
		manual_soil_depths?: any[];
	}

	export interface Sample extends SampleBase {
		sub_samples?: string[];
	}

	/** A custom version of Sample, replacing the sub_sample IDs with the actual SubSample objects */
	export interface HydratedSample extends SampleBase {
		sub_samples: SubSample[];
	}

	export interface SampleEventBase {
		id?: string;
		name?: string;
		collected_on?: string;
		expires_on?: any;
		event_code?: string;
		comments?: any;
		centroid?: string;
		source?: any;
		created_at: string;
		updated_at: string;
		sample_type?: string;
		lab_id?: string;
		account_number?: any;
		creator_id?: string;
		season?: number;
		sub_season?: any;
		deleted_at?: any;
		field_id?: string;
		lab_event_id?: any;
		sample_method?: string;
		sample_density?: number;
		nutrient_count?: number;
		lab_report_ids?: any[];
		lab_recommendation_ids?: any[];
		status?: string;
		raster_locations?: any[];
		sample_event_type?: string;
		sample_event_type_label?: string;
		associations?: Associations;
		has_lab_data?: boolean;
		has_biological_element?: boolean;
		sample_count?: number;
		lab_packages?: any[];
		lab_packages_sample_events?: any[];
		requested_recs?: any[];
		links?: Record<string, string>;
		nutrient_elements?: string[];
		recommendation_elements?: any[];
		soil_nutrients?: any[];
		soil_depths?: string[];
		soil_textures?: any[];
		soil_recommendations?: any[];
		soil_nutrient_summaries?: any[];
		soil_texture_summaries?: any[];
		soil_recommendation_summaries?: any[];
	}

	export interface SampleEvent extends SampleEventBase {
		samples?: string[];
	}

	/** A custom version of SampleEvent, replacing the sample IDs with the actual Sample objects */
	export interface HydratedSampleEvent extends SampleEventBase {
		samples: HydratedSample[];
	}

	export interface SampleEventResponse {
		sample_event?: SampleEvent;
		lab_packages?: any[];
		lab_packages_sample_events?: any[];
		requested_recs?: any[];
		samples?: Sample[];
		nutrient_elements?: NutrientElement[];
		recommendation_elements?: any[];
		soil_nutrients?: any[];
		soil_depths?: SoilDepth[];
		soil_textures?: any[];
		soil_recommendations?: any[];
		soil_nutrient_summaries?: any[];
		soil_texture_summaries?: any[];
		soil_recommendation_summaries?: any[];
		sub_samples?: SubSample[];
		lab_packages_samples?: any[];
		manual_soil_depths?: any[];
		extraction_methods?: ExtractionMethod[];
	}

	export interface SampleEventsResponse {
		sample_events?: SampleEvent[];
		meta?: Meta;
		lab_packages?: LabPackage[];
		lab_packages_sample_events?: string[];
		requested_recs?: any;
	}

	// endregion

	// region TARGET EVENT

	export interface TargetSample {
		id?: string;
		sample_number?: string;
		geometry: FeatureCollection;
		target_event_id?: string;
		links?: Record<string, string>;
	}

	export interface TargetEventBase {
		id?: string;
		name?: string;
		created_at: string;
		updated_at: string;
		associations?: Associations;
		creator_id?: string;
		sample_count?: number;
		target_types?: string[];
		links?: Record<string, string>;
	}

	export interface TargetEvent extends TargetEventBase {
		target_samples: string[];
	}

	/** A custom version of TargetEvent, replacing the target sample IDs with the actual TargetSample objects */
	export interface HydratedTargetEvent extends TargetEventBase {
		target_samples: TargetSample[];
	}

	export interface TargetEventResponse {
		target_events?: TargetEvent[];
		meta?: Meta;
		target_samples?: TargetSample[];
	}

	// endregion
}
